import React, { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  AppBar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Slide,
  Slider,
  Stack,
  Tab,
  Tabs,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material'
import type { TransitionProps } from '@mui/material/transitions'
import {
  IconX,
  IconUpload,
  IconWand,
  IconDownload,
  IconPhotoPlus,
  IconCheck,
  IconFileUpload,
  IconCopy,
  IconChevronDown,
  IconTrash,
} from '@tabler/icons-react'
import type { CardData, CardFormat, ImageGeneratorOrderData } from './types'
import { BUILT_IN_PLACEHOLDER_KEYS, DEFAULT_BRANDING, DEFAULT_JOB } from './types'
import { fileToDataUrl, removeWhiteBackground, elementToPng, dataUrlToFile } from './imageOps'
import {
  DEFAULT_TEMPLATE_ID,
  deleteCustomTemplate,
  getAllTemplates,
  saveCustomTemplate,
  updateCustomTemplateFormat,
} from './templateRegistry'
import { validateTemplate } from './templateValidation'
import { TEMPLATE_HELPER_PROMPT } from './templateHelperPrompt'
import type { TemplateDefinition } from './templateTypes'

// ── Transition ───────────────────────────────────────────────────────────────

const Transition = forwardRef(function Transition(
  props: TransitionProps & { children: React.ReactElement },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />
})

// ── Color field ──────────────────────────────────────────────────────────────

const ColorField: React.FC<{
  label: string
  value: string
  onChange: (v: string) => void
}> = ({ label, value, onChange }) => {
  const safeHex = /^#[0-9A-Fa-f]{6}$/.test(value) ? value : '#000000'
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Box
        component="input"
        type="color"
        value={safeHex}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
        sx={{
          width: 44,
          height: 36,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          p: 0,
          bgcolor: 'transparent',
          cursor: 'pointer',
        }}
      />
      <TextField
        size="small"
        label={label}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        sx={{ flex: 1 }}
        inputProps={{ style: { fontFamily: 'monospace' } }}
      />
    </Stack>
  )
}

// ── Font-size control ────────────────────────────────────────────────────────

/** Preset font sizes shown in the dropdown (mirrors common design-tool presets). */
const FONT_SIZE_PRESETS = [10, 11, 12, 13, 14, 16, 18, 20, 24, 32, 36, 40, 48, 64, 96, 128]

/**
 * Compact px font-size input with a preset dropdown (Figma-style). The user can
 * type any pixel value or pick from the list; clearing it falls back to the
 * template default (`undefined`).
 */
const FontSizeControl: React.FC<{
  value: number | undefined
  onChange: (v: number | undefined) => void
}> = ({ value, onChange }) => {
  const [anchor, setAnchor] = useState<null | HTMLElement>(null)
  return (
    <>
      <TextField
        size="small"
        type="number"
        value={value ?? ''}
        onChange={(e) => {
          const n = parseInt(e.target.value, 10)
          onChange(Number.isFinite(n) ? n : undefined)
        }}
        sx={{ width: 96, flexShrink: 0 }}
        inputProps={{ min: 6, max: 400, 'aria-label': 'Font size (px)' }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end" sx={{ ml: 0 }}>
              <IconButton
                size="small"
                edge="end"
                onClick={(e) => setAnchor(e.currentTarget)}
                aria-label="Font size presets"
              >
                <IconChevronDown size={14} />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        slotProps={{ paper: { sx: { maxHeight: 280 } } }}
      >
        {FONT_SIZE_PRESETS.map((s) => (
          <MenuItem
            key={s}
            dense
            selected={s === value}
            onClick={() => {
              onChange(s)
              setAnchor(null)
            }}
          >
            {s}
          </MenuItem>
        ))}
      </Menu>
    </>
  )
}

// ── Section wrapper ──────────────────────────────────────────────────────────

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <Stack spacing={1.5}>
    <Typography variant="overline" color="text.secondary" fontWeight={600} letterSpacing={0.6}>
      {title}
    </Typography>
    {children}
  </Stack>
)

// ── Color helpers ────────────────────────────────────────────────────────────

/** Convert any CSS color (named, rgb, hsl…) to a #rrggbb hex string via canvas. */
function cssColorToHex(color: string): string | null {
  if (!color) return null
  const ctx = document.createElement('canvas').getContext('2d')
  if (!ctx) return null
  ctx.fillStyle = '#000000'
  ctx.fillStyle = color
  const hex = ctx.fillStyle
  // If canvas couldn't parse it, it stays #000000 — treat as null unless input was black
  if (hex === '#000000' && !/^(#0{3,6}|black)$/i.test(color.trim())) return null
  return hex
}

/** Extract the first inline color from an HTML string (looks at style="color:…"). */
function extractInlineColor(html: string): string | null {
  if (!html || !html.includes('style')) return null
  const tmp = document.createElement('div')
  tmp.innerHTML = html
  const el = tmp.querySelector<HTMLElement>('[style]')
  if (!el) return null
  const raw = el.style.color
  if (!raw) return null
  return cssColorToHex(raw)
}

/** Replace all inline `color:` values inside an HTML string with a new hex color. */
function replaceInlineColor(html: string, newHex: string): string {
  if (!html || !html.includes('style')) return html
  const tmp = document.createElement('div')
  tmp.innerHTML = html
  tmp.querySelectorAll<HTMLElement>('[style]').forEach((el) => {
    if (el.style.color) el.style.color = newHex
  })
  return tmp.innerHTML
}

// ── Defaults ─────────────────────────────────────────────────────────────────

/** Fallback CTA button text used when the user leaves the field empty. */
const DEFAULT_CTA_TEXT = 'Jetzt bewerben'

const BUILT_IN_KEYS = new Set<string>(BUILT_IN_PLACEHOLDER_KEYS)

/** Turn a placeholder id like "start-date" into a label like "Start date". */
function humanizePlaceholderKey(key: string): string {
  const spaced = key.replace(/[-_]+/g, ' ').trim()
  return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}

/** Read the rendered px font-size of every text placeholder in a document. */
function readDefaultFontSizes(doc: Document): Record<string, number> {
  const out: Record<string, number> = {}
  doc.querySelectorAll<HTMLElement>('[data-placeholder]').forEach((el) => {
    const key = el.dataset.placeholder
    if (!key || el.dataset.placeholderType === 'image') return
    const px = parseFloat(doc.defaultView!.getComputedStyle(el).fontSize)
    if (Number.isFinite(px)) out[key] = Math.round(px)
  })
  return out
}

/** Collect non-built-in data-placeholder ids (text only) from a template's HTML. */
function findCustomPlaceholderKeys(html: string): string[] {
  const doc = new DOMParser().parseFromString(html, 'text/html')
  const keys: string[] = []
  doc.querySelectorAll<HTMLElement>('[data-placeholder]').forEach((el) => {
    const key = el.dataset.placeholder
    if (!key || BUILT_IN_KEYS.has(key)) return
    // Skip image placeholders — those need their own upload handling.
    if (el.dataset.placeholderType === 'image') return
    if (!keys.includes(key)) keys.push(key)
  })
  return keys
}

/** Set of every data-placeholder id present in a template's HTML. */
function findPresentPlaceholderKeys(html: string): Set<string> {
  const doc = new DOMParser().parseFromString(html, 'text/html')
  const keys = new Set<string>()
  doc.querySelectorAll<HTMLElement>('[data-placeholder]').forEach((el) => {
    const key = el.dataset.placeholder
    if (key) keys.add(key)
  })
  return keys
}

// ── Main dialog component ────────────────────────────────────────────────────

export interface ImageGeneratorDialogProps {
  open: boolean
  onClose: () => void
  orderData?: ImageGeneratorOrderData
  onAddToCampaign?: (file: File, format: CardFormat) => void | Promise<void>
}

const ImageGeneratorDialog: React.FC<ImageGeneratorDialogProps> = ({
  open,
  onClose,
  orderData,
  onAddToCampaign,
}) => {
  // ── State ──
  const [format, setFormat] = useState<CardFormat>('feed')
  const [data, setData] = useState<CardData>(() => buildInitialData(orderData))
  const [templates, setTemplates] = useState<TemplateDefinition[]>(() => getAllTemplates())
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>(DEFAULT_TEMPLATE_ID)
  const [iframeReady, setIframeReady] = useState(false)
  // Template's own px font sizes per field for the current format — used to seed
  // the font-size inputs so they show the real value until the user overrides it.
  const [defaultFontSizes, setDefaultFontSizes] = useState<Record<string, number>>({})
  const [templateErrors, setTemplateErrors] = useState<string[]>([])
  const [validating, setValidating] = useState(false)
  const [customName, setCustomName] = useState('')
  const [helperOpen, setHelperOpen] = useState(false)
  const [promptCopied, setPromptCopied] = useState(false)
  const [bgRemoving, setBgRemoving] = useState(false)
  const [bgError, setBgError] = useState<string | null>(null)
  const [exporting, setExporting] = useState(false)
  const [adding, setAdding] = useState(false)
  const [addSuccess, setAddSuccess] = useState(false)
  const [logoFileName, setLogoFileName] = useState<string | null>(null)
  const [photoFileName, setPhotoFileName] = useState<string | null>(null)
  const [originalLogoUrl, setOriginalLogoUrl] = useState<string | null>(null)

  // ── Refs ──
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const templateFileRef = useRef<HTMLInputElement>(null)
  const photoFileRef = useRef<HTMLInputElement>(null)
  const logoFileRef = useRef<HTMLInputElement>(null)
  const prevOpenRef = useRef(false)
  const pendingExtractRef = useRef(false)
  const dataRef = useRef(data)
  dataRef.current = data
  // Order/customer-derived prefill values (kept current for the extract pass).
  const orderPrefillRef = useRef<ImageGeneratorOrderData>(orderData ?? {})
  orderPrefillRef.current = orderData ?? {}

  // ── Derived ──
  const selectedTemplate = templates.find((t) => t.id === selectedTemplateId) ?? templates[0]
  const currentTemplate = selectedTemplate.templates[format]
  // Extra text placeholders the current template exposes beyond the built-ins.
  const customKeys = useMemo(() => findCustomPlaceholderKeys(currentTemplate), [currentTemplate])
  // Which placeholder ids the current template actually contains. Used to hide
  // optional built-in fields (e.g. Company) for templates that don't render them.
  const presentKeys = useMemo(() => findPresentPlaceholderKeys(currentTemplate), [currentTemplate])

  // ── Re-initialize when dialog opens ──
  useEffect(() => {
    if (open && !prevOpenRef.current) {
      setData(buildInitialData(orderData))
      setFormat('feed')
      setBgError(null)
      setTemplateErrors([])
      setCustomName('')
      setHelperOpen(false)
      setLogoFileName(null)
      setPhotoFileName(null)
      setOriginalLogoUrl(null)
      setTemplates(getAllTemplates())
      setSelectedTemplateId(DEFAULT_TEMPLATE_ID)
      // Extract colors from template on first load
      pendingExtractRef.current = true
    }
    prevOpenRef.current = open
  }, [open]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Helpers to update state ──
  const set = <K extends keyof CardData>(k: K, v: CardData[K]) => setData((d) => ({ ...d, [k]: v }))
  const setJob = (k: keyof CardData['job'], v: string) =>
    setData((d) => ({ ...d, job: { ...d.job, [k]: v } }))
  const setBrand = (k: keyof CardData['brand'], v: string) =>
    setData((d) => ({ ...d, brand: { ...d.brand, [k]: v } }))
  const setCustomField = (key: string, v: string) =>
    setData((d) => ({ ...d, customFields: { ...(d.customFields || {}), [key]: v } }))
  const setFontSize = (key: string, v: number | undefined) =>
    setData((d) => {
      const next = { ...(d.fontSizes || {}) }
      if (v == null) delete next[key]
      else next[key] = v
      return { ...d, fontSizes: next }
    })

  // ── Apply data to iframe DOM via data-placeholder attributes ──
  const applyDataToIframe = useCallback((d: CardData) => {
    const doc = iframeRef.current?.contentDocument
    if (!doc) return

    const placeholders: Record<string, string> = {
      title: d.displayTitle,
      label: d.label || '',
      subtitle: d.subtitle || '',
      city: d.job.city,
      'employment-type': d.job.employmentType,
      company: d.job.company,
      'cta-text': d.ctaText?.trim() ? d.ctaText : DEFAULT_CTA_TEXT,
      logo: d.job.logoUrl,
      'header-image': d.job.headerImage,
      // Generic custom placeholders defined by the template (e.g. start-date).
      ...(d.customFields || {}),
    }

    const fontSizes = d.fontSizes || {}

    doc.querySelectorAll<HTMLElement>('[data-placeholder]').forEach((el) => {
      const key = el.dataset.placeholder!
      const type = el.dataset.placeholderType || 'html'
      const value = placeholders[key]

      if (type === 'image') {
        if (value === undefined) return
        if (value) el.setAttribute('src', value)
        else el.removeAttribute('src')
      } else {
        if (value !== undefined) {
          // All text placeholders support HTML so users can style inline
          el.innerHTML = value
        }
        // Per-field font size: explicit px override, else fall back to template CSS.
        const fs = fontSizes[key]
        el.style.fontSize = fs != null ? `${fs}px` : ''
      }
    })

    // Colors via CSS custom properties
    const root = doc.documentElement
    root.style.setProperty('--primary-color', d.brand.primary)
    root.style.setProperty('--accent-color', d.brand.accent)
    root.style.setProperty('--title-color', d.brand.titleColor)
    root.style.setProperty('--cta-text-color', d.brand.ctaTextColor)
    root.style.setProperty('--stroke-color', d.brand.strokeColor)

    // CTA button corner radius (px). Falls back to the template's own radius.
    // The slider max (60) means "fully rounded" → pill / half-circle ends.
    const ctaBtn = doc.querySelector<HTMLElement>('.cta-btn')
    if (ctaBtn) {
      ctaBtn.style.borderRadius =
        d.ctaRadius == null ? '' : d.ctaRadius >= 60 ? '9999px' : `${d.ctaRadius}px`
    }

    // Layout overrides
    const logoEl = doc.querySelector<HTMLImageElement>('[data-placeholder="logo"]')
    if (logoEl && d.logoSize != null) {
      logoEl.style.maxWidth = `${d.logoSize}px`
      logoEl.style.maxHeight = `${d.logoSize}px`
    }
    const heroEl = doc.querySelector<HTMLImageElement>('[data-placeholder="header-image"]')
    if (heroEl) {
      const ox = d.photoOffsetX ?? 50
      const oy = d.photoOffsetY ?? 50
      const zoom = d.photoZoom ?? 1
      heroEl.style.objectPosition = `${ox}% ${oy}%`
      // Zoom past the frame so there's always overflow for the offsets to pan,
      // even when the photo and the (square) frame share the same aspect ratio.
      heroEl.style.transform = zoom > 1 ? `scale(${zoom})` : ''
      heroEl.style.transformOrigin = `${ox}% ${oy}%`
    }
  }, [])

  // ── Extract field values from iframe DOM into state via data-placeholder ──
  const extractFieldsFromIframe = useCallback(() => {
    const doc = iframeRef.current?.contentDocument
    if (!doc) return

    const getPlaceholder = (key: string): string => {
      const el = doc.querySelector<HTMLElement>(`[data-placeholder="${key}"]`)
      if (!el) return ''
      const type = el.dataset.placeholderType || 'html'
      if (type === 'image') return el.getAttribute('src') || ''
      return el.innerHTML.trim()
    }

    const getCssVar = (name: string, fallback: string) => {
      try {
        return getComputedStyle(doc.documentElement).getPropertyValue(name).trim() || fallback
      } catch {
        return fallback
      }
    }

    // Read any custom (non-built-in) placeholders' default content from the
    // template, so their inputs start pre-filled with whatever the author wrote.
    const customFields: Record<string, string> = {}
    doc.querySelectorAll<HTMLElement>('[data-placeholder]').forEach((el) => {
      const key = el.dataset.placeholder
      if (!key || BUILT_IN_KEYS.has(key) || el.dataset.placeholderType === 'image') return
      customFields[key] = el.innerHTML.trim()
    })

    // Order/customer-derived values take precedence over the template's own
    // placeholder content and default images, so opening from an order keeps
    // the customer logo, company name, title, etc.
    const order = orderPrefillRef.current
    setData((prev) => ({
      ...prev,
      // Keep any values the user already typed; fall back to template defaults.
      customFields: { ...customFields, ...(prev.customFields || {}) },
      displayTitle: order.jobName || getPlaceholder('title') || prev.displayTitle,
      label: getPlaceholder('label') || prev.label,
      subtitle: getPlaceholder('subtitle') || prev.subtitle,
      ctaText: getPlaceholder('cta-text') || prev.ctaText,
      job: {
        ...prev.job,
        title: order.jobName || getPlaceholder('title') || prev.job.title,
        city: order.city || getPlaceholder('city') || prev.job.city,
        // Company is intentionally left empty by default — it inherits neither
        // the order/customer name nor the template's placeholder text.
        company: prev.job.company,
        employmentType:
          order.employmentType || getPlaceholder('employment-type') || prev.job.employmentType,
        logoUrl: order.logoUrl || getPlaceholder('logo') || prev.job.logoUrl,
        headerImage: order.headerImage || getPlaceholder('header-image') || prev.job.headerImage,
      },
      brand: {
        primary: getCssVar('--primary-color', prev.brand.primary),
        accent: getCssVar('--accent-color', prev.brand.accent),
        titleColor: getCssVar('--title-color', prev.brand.titleColor),
        ctaTextColor: getCssVar('--cta-text-color', prev.brand.ctaTextColor),
        strokeColor: getCssVar('--stroke-color', prev.brand.strokeColor),
      },
    }))
  }, [])

  // ── Bidirectional sync: inline CSS color ↔ brand color picker ──

  // Title HTML → titleColor picker
  useEffect(() => {
    const color = extractInlineColor(data.displayTitle)
    if (color && color !== data.brand.titleColor) {
      setBrand('titleColor', color)
    }
  }, [data.displayTitle]) // eslint-disable-line react-hooks/exhaustive-deps

  // titleColor picker → Title HTML
  useEffect(() => {
    if (!data.displayTitle.includes('style')) return
    const current = extractInlineColor(data.displayTitle)
    if (current && current !== data.brand.titleColor) {
      set('displayTitle', replaceInlineColor(data.displayTitle, data.brand.titleColor))
    }
  }, [data.brand.titleColor]) // eslint-disable-line react-hooks/exhaustive-deps

  // CTA HTML → ctaTextColor picker
  useEffect(() => {
    const color = extractInlineColor(data.ctaText || '')
    if (color && color !== data.brand.ctaTextColor) {
      setBrand('ctaTextColor', color)
    }
  }, [data.ctaText]) // eslint-disable-line react-hooks/exhaustive-deps

  // ctaTextColor picker → CTA HTML
  useEffect(() => {
    if (!data.ctaText?.includes('style')) return
    const current = extractInlineColor(data.ctaText || '')
    if (current && current !== data.brand.ctaTextColor) {
      set('ctaText', replaceInlineColor(data.ctaText || '', data.brand.ctaTextColor))
    }
  }, [data.brand.ctaTextColor]) // eslint-disable-line react-hooks/exhaustive-deps

  // City / Employment-type HTML → primary color (icons)
  useEffect(() => {
    const color = extractInlineColor(data.job.city)
    if (color && color !== data.brand.primary) {
      setBrand('primary', color)
    }
  }, [data.job.city]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const color = extractInlineColor(data.job.employmentType)
    if (color && color !== data.brand.primary) {
      setBrand('primary', color)
    }
  }, [data.job.employmentType]) // eslint-disable-line react-hooks/exhaustive-deps

  // Primary color picker → City + Employment-type HTML
  useEffect(() => {
    if (data.job.city.includes('style')) {
      const current = extractInlineColor(data.job.city)
      if (current && current !== data.brand.primary) {
        setJob('city', replaceInlineColor(data.job.city, data.brand.primary))
      }
    }
    if (data.job.employmentType.includes('style')) {
      const current = extractInlineColor(data.job.employmentType)
      if (current && current !== data.brand.primary) {
        setJob('employmentType', replaceInlineColor(data.job.employmentType, data.brand.primary))
      }
    }
  }, [data.brand.primary]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Iframe load handler ──
  const handleIframeLoad = useCallback(() => {
    setIframeReady(true)
    // Capture the template's native px sizes (before any overrides are applied)
    // so the font-size inputs reflect the real per-format values.
    const doc = iframeRef.current?.contentDocument
    if (doc) setDefaultFontSizes(readDefaultFontSizes(doc))
    if (pendingExtractRef.current) {
      pendingExtractRef.current = false
      extractFieldsFromIframe()
    } else {
      applyDataToIframe(dataRef.current)
    }
  }, [applyDataToIframe, extractFieldsFromIframe])

  // ── When data changes, update iframe DOM ──
  useEffect(() => {
    if (iframeReady) applyDataToIframe(data)
  }, [data, iframeReady, applyDataToIframe])

  // ── When template changes, iframe reloads — reset ready flag ──
  useEffect(() => {
    setIframeReady(false)
  }, [currentTemplate])

  // ── Template upload (validate → persist → select) ──
  const handleTemplateUpload = async (file: File | null) => {
    if (!file) return
    setTemplateErrors([])
    setBgError(null)
    setValidating(true)

    try {
      const html = await file.text()
      const result = await validateTemplate(html, format)
      if (!result.valid) {
        setTemplateErrors(result.errors)
        return
      }

      const selected = templates.find((t) => t.id === selectedTemplateId)
      pendingExtractRef.current = true

      if (selected?.isCustom) {
        // Add this format's HTML to the already-selected custom template.
        const updated = updateCustomTemplateFormat(selected.id, format, html)
        if (updated) {
          setTemplates(getAllTemplates())
          setSelectedTemplateId(updated.id)
        }
      } else {
        // Create a new custom template from this upload.
        const name = customName.trim() || `Custom ${new Date().toLocaleDateString()}`
        const def = saveCustomTemplate(name, format, html)
        setTemplates(getAllTemplates())
        setSelectedTemplateId(def.id)
        setCustomName('')
      }
    } catch (e: any) {
      setTemplateErrors([`Could not read template file: ${e?.message ?? 'unknown error'}`])
    } finally {
      setValidating(false)
    }
  }

  // ── Template selection + helper prompt ──
  const handleSelectTemplate = (id: string) => {
    setTemplateErrors([])
    pendingExtractRef.current = true
    setSelectedTemplateId(id)
  }

  const handleDeleteTemplate = (id: string) => {
    deleteCustomTemplate(id)
    setTemplates(getAllTemplates())
    if (selectedTemplateId === id) {
      pendingExtractRef.current = true
      setSelectedTemplateId(DEFAULT_TEMPLATE_ID)
    }
  }

  const handleCopyPrompt = async () => {
    try {
      await navigator.clipboard.writeText(TEMPLATE_HELPER_PROMPT)
      setPromptCopied(true)
      setTimeout(() => setPromptCopied(false), 1500)
    } catch {
      /* clipboard unavailable — user can still select the text manually */
    }
  }

  // ── Photo / Logo upload ──
  const handlePhotoUpload = async (file: File | null) => {
    if (!file) return
    try {
      const dataUrl = await fileToDataUrl(file, { maxSize: 1600, quality: 0.86 })
      setJob('headerImage', dataUrl)
      setPhotoFileName(file.name)
    } catch (e: any) {
      setBgError(`Photo upload failed: ${e?.message ?? 'unknown'}`)
    }
  }

  const handleLogoUpload = async (file: File | null) => {
    if (!file) return
    try {
      const dataUrl = await fileToDataUrl(file, { maxSize: 800, quality: 0.92 })
      setJob('logoUrl', dataUrl)
      setLogoFileName(file.name)
      setOriginalLogoUrl(null)
    } catch (e: any) {
      setBgError(`Logo upload failed: ${e?.message ?? 'unknown'}`)
    }
  }

  // ── Background removal ──
  const handleBgRemove = async () => {
    if (!data.job.logoUrl) {
      setBgError('Please enter or upload a logo first.')
      return
    }
    setBgRemoving(true)
    setBgError(null)
    try {
      setOriginalLogoUrl(data.job.logoUrl)
      const dataUrl = await removeWhiteBackground(data.job.logoUrl)
      setJob('logoUrl', dataUrl)
    } catch (e: any) {
      setBgError(e?.message ?? 'Background removal failed.')
    } finally {
      setBgRemoving(false)
    }
  }

  const handleBgRestore = () => {
    if (!originalLogoUrl) return
    setJob('logoUrl', originalLogoUrl)
    setOriginalLogoUrl(null)
  }

  // ── Rasterize swoosh SVGs to PNG <img> for html2canvas compatibility ──
  const resolveSvgsForExport = async (doc: Document) => {
    const style = getComputedStyle(doc.documentElement)
    const vars: Record<string, string> = {
      '--primary-color': style.getPropertyValue('--primary-color').trim(),
      '--accent-color': style.getPropertyValue('--accent-color').trim(),
      '--stroke-color': style.getPropertyValue('--stroke-color').trim(),
      '--cta-text-color': style.getPropertyValue('--cta-text-color').trim(),
      '--title-color': style.getPropertyValue('--title-color').trim(),
    }
    const resolve = (val: string): string =>
      val.replace(/var\(\s*(--[\w-]+)\s*\)/g, (_, name) => vars[name] || val)

    // Rasterize swoosh SVGs to PNG via canvas (html2canvas can't handle inline SVG gradients)
    const svgs = Array.from(doc.querySelectorAll<SVGElement>('svg.swoosh-top, svg.swoosh-bottom'))
    for (const svg of svgs) {
      // Resolve CSS vars
      svg.querySelectorAll('stop').forEach((stop) => {
        const sc = stop.getAttribute('stop-color')
        if (sc && sc.includes('var(')) stop.setAttribute('stop-color', resolve(sc))
      })
      svg.querySelectorAll('path, circle').forEach((el) => {
        const fill = el.getAttribute('fill')
        if (fill && fill.includes('var(')) el.setAttribute('fill', resolve(fill))
        const stroke = el.getAttribute('stroke')
        if (stroke && stroke.includes('var(')) el.setAttribute('stroke', resolve(stroke))
      })

      // Get pixel dimensions from viewBox
      const vb = svg.getAttribute('viewBox')?.split(/\s+/).map(Number) || [0, 0, 800, 800]
      const w = vb[2]
      const h = vb[3]

      // Set explicit width/height for standalone rendering
      svg.setAttribute('width', String(w))
      svg.setAttribute('height', String(h))

      // Serialize and render to canvas
      const serializer = new XMLSerializer()
      const svgStr = serializer.serializeToString(svg)
      const blob = new Blob([svgStr], { type: 'image/svg+xml;charset=utf-8' })
      const url = URL.createObjectURL(blob)

      const tempImg = new Image()
      await new Promise<void>((r) => {
        tempImg.onload = () => r()
        tempImg.onerror = () => r()
        tempImg.src = url
      })

      const canvas = document.createElement('canvas')
      canvas.width = w
      canvas.height = h
      const ctx = canvas.getContext('2d')!
      ctx.drawImage(tempImg, 0, 0, w, h)
      URL.revokeObjectURL(url)

      const pngDataUrl = canvas.toDataURL('image/png')

      // Replace SVG with rasterized <img>
      const imgEl = doc.createElement('img')
      imgEl.src = pngDataUrl
      imgEl.className = svg.getAttribute('class') || ''
      svg.parentNode?.replaceChild(imgEl, svg)
    }

    // Resolve CSS vars in remaining inline SVGs (icons, arrow)
    doc.querySelectorAll('svg path, svg circle').forEach((el) => {
      const fill = el.getAttribute('fill')
      if (fill && fill.includes('var(')) el.setAttribute('fill', resolve(fill))
      const stroke = el.getAttribute('stroke')
      if (stroke && stroke.includes('var(')) el.setAttribute('stroke', resolve(stroke))
    })
  }

  // ── Render full-size PNG via off-screen iframe ──
  const renderFullSizePng = async (): Promise<string> => {
    const isFeed = format === 'feed'
    const w = 1080
    const h = isFeed ? 1080 : 1920

    const doc = iframeRef.current?.contentDocument
    if (!doc) throw new Error('Template not loaded')

    // Serialize current state (includes inline styles + text changes)
    const fullHtml = `<!DOCTYPE html>${doc.documentElement.outerHTML}`

    // Off-screen iframe at full resolution
    const tempIframe = document.createElement('iframe')
    tempIframe.style.cssText = `position:fixed;left:-9999px;top:0;width:${w}px;height:${h}px;border:none;`
    document.body.appendChild(tempIframe)

    const tempDoc = tempIframe.contentDocument!
    tempDoc.open()
    tempDoc.write(fullHtml)
    tempDoc.close()

    // Wait for fonts + images
    try {
      await tempDoc.fonts.ready
    } catch {
      /* ignore */
    }
    await new Promise<void>((r) => {
      setTimeout(r, 1200)
    })

    // Rasterize swoosh SVGs to PNG and resolve CSS vars (html2canvas can't render inline SVGs with gradients)
    await resolveSvgsForExport(tempDoc)

    const mainEl = tempDoc.querySelector('.main') as HTMLElement
    if (!mainEl) throw new Error('.main element not found in template')

    try {
      const pngUrl = await elementToPng(mainEl)
      return pngUrl
    } finally {
      document.body.removeChild(tempIframe)
    }
  }

  // ── Download / Add to campaign ──
  const handleDownload = async () => {
    setExporting(true)
    setBgError(null)
    try {
      const pngUrl = await renderFullSizePng()
      const link = document.createElement('a')
      link.href = pngUrl
      link.download = `card-${format}.png`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    } catch (err: any) {
      console.error('Export error:', err)
      setBgError(`Failed to export image: ${err?.message || 'Please try again.'}`)
    } finally {
      setExporting(false)
    }
  }

  const handleAddToCampaign = async () => {
    if (!onAddToCampaign) return
    setAdding(true)
    setBgError(null)
    try {
      const pngUrl = await renderFullSizePng()
      const filename = `generated-${format}-${Date.now()}.png`
      const file = await dataUrlToFile(pngUrl, filename)
      await onAddToCampaign(file, format)
      setAdding(false)
      setAddSuccess(true)
      setTimeout(() => setAddSuccess(false), 2000)
    } catch (err: any) {
      console.error('Add to campaign error:', err)
      setBgError(`Failed to add image to campaign: ${err?.message || 'Please try again.'}`)
      setAdding(false)
    }
  }

  // ── Preview dimensions ──
  const isFeed = format === 'feed'
  const cardW = 1080
  const cardH = isFeed ? 1080 : 1920
  const scale = isFeed ? 0.55 : 0.42
  const wrapperW = cardW * scale
  const wrapperH = cardH * scale

  // ── Render ──
  return (
    <Dialog fullScreen open={open} onClose={onClose} TransitionComponent={Transition}>
      <AppBar sx={{ position: 'sticky' }} color="default" elevation={1}>
        <Toolbar>
          <IconButton edge="start" onClick={onClose}>
            <IconX />
          </IconButton>
          <Typography sx={{ ml: 2, flex: 1, textAlign: 'center' }} variant="h4">
            Image Generator
          </Typography>
          <Box sx={{ width: 48 }} />
        </Toolbar>
      </AppBar>

      <Box sx={{ display: 'flex', flexDirection: 'row-reverse', flex: 1, overflow: 'hidden' }}>
        {/* ── Sidebar ── */}
        <Box
          sx={{
            width: 380,
            p: 3,
            pb: 12,
            borderLeft: 1,
            borderColor: 'divider',
            overflowY: 'auto',
            bgcolor: 'background.paper',
          }}
        >
          <Stack spacing={2.5}>
            {/* Format tabs */}
            <Tabs
              value={format}
              onChange={(_, v) => {
                // Keep all current data/colors when switching format — the new
                // format's iframe is re-hydrated from state, not re-extracted.
                setFormat(v)
              }}
              variant="fullWidth"
              sx={{
                bgcolor: 'grey.100',
                borderRadius: 1.5,
                minHeight: 40,
                '& .MuiTab-root': { minHeight: 40, fontWeight: 600 },
              }}
            >
              <Tab label="Feed" value="feed" />
              <Tab label="Story" value="story" />
            </Tabs>

            {/* ── Template ── */}
            <Section title="Template">
              <TextField
                select
                size="small"
                label="Template"
                fullWidth
                value={selectedTemplate.id}
                onChange={(e) => handleSelectTemplate(e.target.value)}
              >
                {templates.map((t) => (
                  <MenuItem key={t.id} value={t.id}>
                    {t.name}
                    {t.isCustom ? ' (custom)' : ''}
                  </MenuItem>
                ))}
              </TextField>

              {selectedTemplate.isCustom && (
                <Button
                  variant="text"
                  size="small"
                  color="error"
                  startIcon={<IconTrash size={14} />}
                  onClick={() => handleDeleteTemplate(selectedTemplate.id)}
                  sx={{ alignSelf: 'flex-start' }}
                >
                  Delete this template
                </Button>
              )}

              {/* Custom template upload */}
              <Accordion
                disableGutters
                elevation={0}
                sx={{
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                  '&:before': { display: 'none' },
                }}
              >
                <AccordionSummary expandIcon={<IconChevronDown size={18} />}>
                  <Typography variant="body2" fontWeight={600}>
                    Upload custom template
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack spacing={1.5}>
                    <Typography variant="caption" color="text.secondary">
                      Upload an HTML file for the <strong>{format}</strong> format (
                      {format === 'feed' ? '1080×1080' : '1080×1920'}). It must contain the
                      placeholders: title, city, cta-text, logo, header-image.
                    </Typography>

                    {!selectedTemplate.isCustom && (
                      <TextField
                        size="small"
                        label="Template name"
                        fullWidth
                        value={customName}
                        onChange={(e) => setCustomName(e.target.value)}
                        placeholder="My template"
                      />
                    )}

                    <Button
                      variant="outlined"
                      size="small"
                      fullWidth
                      startIcon={
                        validating ? <CircularProgress size={14} /> : <IconFileUpload size={16} />
                      }
                      disabled={validating}
                      onClick={() => templateFileRef.current?.click()}
                    >
                      {validating ? 'Validating…' : `Upload ${format} HTML`}
                    </Button>
                    <input
                      ref={templateFileRef}
                      type="file"
                      accept=".html,text/html"
                      hidden
                      onChange={(e) => {
                        handleTemplateUpload(e.target.files?.[0] ?? null)
                        e.target.value = ''
                      }}
                    />

                    {templateErrors.length > 0 && (
                      <Box>
                        {templateErrors.map((err, i) => (
                          <Typography
                            key={i}
                            variant="caption"
                            color="error"
                            sx={{ display: 'block' }}
                          >
                            • {err}
                          </Typography>
                        ))}
                      </Box>
                    )}

                    {/* LLM helper prompt */}
                    <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 1.5 }}>
                      <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <Typography variant="caption" fontWeight={600} color="text.secondary">
                          Generate one with AI
                        </Typography>
                        <Button
                          variant="text"
                          size="small"
                          onClick={() => setHelperOpen((v) => !v)}
                        >
                          {helperOpen ? 'Hide prompt' : 'Show prompt'}
                        </Button>
                      </Stack>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block' }}
                      >
                        Paste this prompt into an LLM to generate a compatible template, then upload
                        the result above.
                      </Typography>
                      {helperOpen && (
                        <Box sx={{ mt: 1 }}>
                          <Button
                            variant="outlined"
                            size="small"
                            fullWidth
                            color={promptCopied ? 'success' : 'primary'}
                            startIcon={
                              promptCopied ? <IconCheck size={16} /> : <IconCopy size={16} />
                            }
                            onClick={handleCopyPrompt}
                            sx={{ mb: 1 }}
                          >
                            {promptCopied ? 'Copied!' : 'Copy prompt'}
                          </Button>
                          <TextField
                            multiline
                            fullWidth
                            minRows={6}
                            maxRows={14}
                            value={TEMPLATE_HELPER_PROMPT}
                            InputProps={{ readOnly: true }}
                            inputProps={{
                              style: { fontFamily: 'monospace', fontSize: 11, lineHeight: 1.4 },
                            }}
                          />
                        </Box>
                      )}
                    </Box>
                  </Stack>
                </AccordionDetails>
              </Accordion>
            </Section>

            {/* ── Content ── */}
            <Section title="Content">
              {presentKeys.has('label') && (
                <Stack direction="row" spacing={1} alignItems="flex-start">
                  <TextField
                    size="small"
                    label="Label"
                    fullWidth
                    value={data.label ?? ''}
                    onChange={(e) => set('label', e.target.value)}
                    placeholder="Wir suchen:"
                  />
                  <FontSizeControl
                    value={data.fontSizes?.label ?? defaultFontSizes.label}
                    onChange={(v) => setFontSize('label', v)}
                  />
                </Stack>
              )}
              <Stack direction="row" spacing={1} alignItems="flex-start">
                <TextField
                  size="small"
                  label="Title"
                  fullWidth
                  value={data.displayTitle}
                  onChange={(e) => set('displayTitle', e.target.value)}
                />
                <FontSizeControl
                  value={data.fontSizes?.title ?? defaultFontSizes.title}
                  onChange={(v) => setFontSize('title', v)}
                />
              </Stack>
              {presentKeys.has('subtitle') && (
                <Stack direction="row" spacing={1} alignItems="flex-start">
                  <TextField
                    size="small"
                    label="Subtitle"
                    fullWidth
                    value={data.subtitle ?? ''}
                    onChange={(e) => set('subtitle', e.target.value)}
                    placeholder="Optional subtitle"
                  />
                  <FontSizeControl
                    value={data.fontSizes?.subtitle ?? defaultFontSizes.subtitle}
                    onChange={(v) => setFontSize('subtitle', v)}
                  />
                </Stack>
              )}
              {presentKeys.has('company') && (
                <Stack direction="row" spacing={1} alignItems="flex-start">
                  <TextField
                    size="small"
                    label="Company"
                    fullWidth
                    value={data.job.company}
                    onChange={(e) => setJob('company', e.target.value)}
                  />
                  <FontSizeControl
                    value={data.fontSizes?.company ?? defaultFontSizes.company}
                    onChange={(v) => setFontSize('company', v)}
                  />
                </Stack>
              )}
              <Stack direction="row" spacing={1} alignItems="flex-start">
                <TextField
                  size="small"
                  label="City"
                  fullWidth
                  value={data.job.city}
                  onChange={(e) => setJob('city', e.target.value)}
                />
                <FontSizeControl
                  value={data.fontSizes?.city ?? defaultFontSizes.city}
                  onChange={(v) => setFontSize('city', v)}
                />
              </Stack>
              {presentKeys.has('employment-type') && (
                <Stack direction="row" spacing={1} alignItems="flex-start">
                  <TextField
                    size="small"
                    label="Employment type"
                    fullWidth
                    value={data.job.employmentType}
                    onChange={(e) => setJob('employmentType', e.target.value)}
                    placeholder="Full-time"
                  />
                  <FontSizeControl
                    value={data.fontSizes?.['employment-type'] ?? defaultFontSizes['employment-type']}
                    onChange={(v) => setFontSize('employment-type', v)}
                  />
                </Stack>
              )}
              <Stack direction="row" spacing={1} alignItems="flex-start">
                <TextField
                  size="small"
                  label="CTA text"
                  fullWidth
                  value={data.ctaText ?? ''}
                  onChange={(e) => set('ctaText', e.target.value)}
                  placeholder="Jetzt bewerben"
                />
                <FontSizeControl
                  value={data.fontSizes?.['cta-text'] ?? defaultFontSizes['cta-text']}
                  onChange={(v) => setFontSize('cta-text', v)}
                />
              </Stack>
              <Stack spacing={0.5}>
                <Typography variant="caption" color="text.secondary">
                  CTA button radius: {(data.ctaRadius ?? 60) >= 60 ? 'Full' : `${data.ctaRadius}px`}
                </Typography>
                <Slider
                  size="small"
                  min={0}
                  max={60}
                  value={data.ctaRadius ?? 60}
                  onChange={(_, v) => set('ctaRadius', v as number)}
                />
              </Stack>
            </Section>

            {/* ── Custom fields from the template ── */}
            {customKeys.length > 0 && (
              <Section title="Template Fields">
                {customKeys.map((key) => (
                  <Stack key={key} direction="row" spacing={1} alignItems="flex-start">
                    <TextField
                      size="small"
                      label={humanizePlaceholderKey(key)}
                      fullWidth
                      value={data.customFields?.[key] ?? ''}
                      onChange={(e) => setCustomField(key, e.target.value)}
                    />
                    <FontSizeControl
                      value={data.fontSizes?.[key] ?? defaultFontSizes[key]}
                      onChange={(v) => setFontSize(key, v)}
                    />
                  </Stack>
                ))}
              </Section>
            )}

            {/* ── Logo ── */}
            <Section title="Logo">
              <TextField
                size="small"
                label="Logo URL"
                fullWidth
                value={
                  data.job.logoUrl.startsWith('data:')
                    ? logoFileName || 'Uploaded logo'
                    : data.job.logoUrl
                }
                onChange={(e) => setJob('logoUrl', e.target.value)}
                placeholder="https://..."
                InputProps={{ readOnly: data.job.logoUrl.startsWith('data:') }}
              />
              <Stack direction="row" spacing={1}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<IconUpload size={16} />}
                  onClick={() => logoFileRef.current?.click()}
                  sx={{ flex: 1 }}
                >
                  Upload
                </Button>
                <input
                  ref={logoFileRef}
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={(e) => handleLogoUpload(e.target.files?.[0] ?? null)}
                />
                {originalLogoUrl ? (
                  <Tooltip title="Restore original logo with background">
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<IconWand size={16} />}
                      onClick={handleBgRestore}
                      sx={{ flex: 1 }}
                    >
                      Add BG
                    </Button>
                  </Tooltip>
                ) : (
                  <Tooltip title="Remove white background from logo">
                    <span>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={
                          bgRemoving ? <CircularProgress size={14} /> : <IconWand size={16} />
                        }
                        onClick={handleBgRemove}
                        disabled={bgRemoving || !data.job.logoUrl}
                        sx={{ flex: 1 }}
                      >
                        {bgRemoving ? '...' : 'Remove BG'}
                      </Button>
                    </span>
                  </Tooltip>
                )}
              </Stack>
              <Stack spacing={0.5}>
                <Typography variant="caption" color="text.secondary">
                  Logo size: {data.logoSize ?? 'auto'}
                </Typography>
                <Slider
                  size="small"
                  min={20}
                  max={300}
                  value={data.logoSize ?? 100}
                  onChange={(_, v) => set('logoSize', v as number)}
                />
              </Stack>
              {bgError && (
                <Typography variant="caption" color="error">
                  {bgError}
                </Typography>
              )}
            </Section>

            {/* ── Photo ── */}
            <Section title="Photo">
              <TextField
                size="small"
                label="Photo URL"
                fullWidth
                value={
                  data.job.headerImage.startsWith('data:')
                    ? photoFileName || 'Uploaded photo'
                    : data.job.headerImage
                }
                onChange={(e) => setJob('headerImage', e.target.value)}
                placeholder="https://..."
                InputProps={{ readOnly: data.job.headerImage.startsWith('data:') }}
              />
              <Button
                variant="outlined"
                size="small"
                fullWidth
                startIcon={<IconUpload size={16} />}
                onClick={() => photoFileRef.current?.click()}
              >
                Upload
              </Button>
              <input
                ref={photoFileRef}
                type="file"
                accept="image/*"
                hidden
                onChange={(e) => handlePhotoUpload(e.target.files?.[0] ?? null)}
              />
              <Stack spacing={0.5}>
                <Typography variant="caption" color="text.secondary">
                  Zoom: {(data.photoZoom ?? 1).toFixed(1)}×
                </Typography>
                <Slider
                  size="small"
                  min={1}
                  max={3}
                  step={0.1}
                  value={data.photoZoom ?? 1}
                  onChange={(_, v) => set('photoZoom', v as number)}
                />
              </Stack>
              <Stack spacing={0.5}>
                <Typography variant="caption" color="text.secondary">
                  Horizontal offset: {data.photoOffsetX ?? 50}%
                </Typography>
                <Slider
                  size="small"
                  min={0}
                  max={100}
                  value={data.photoOffsetX ?? 50}
                  onChange={(_, v) => set('photoOffsetX', v as number)}
                />
              </Stack>
              <Stack spacing={0.5}>
                <Typography variant="caption" color="text.secondary">
                  Vertical offset: {data.photoOffsetY ?? 50}%
                </Typography>
                <Slider
                  size="small"
                  min={0}
                  max={100}
                  value={data.photoOffsetY ?? 50}
                  onChange={(_, v) => set('photoOffsetY', v as number)}
                />
              </Stack>
            </Section>

            {/* ── Colors ── */}
            <Section title="Colors">
              <ColorField
                label="Primary"
                value={data.brand.primary}
                onChange={(v) => setBrand('primary', v)}
              />
              <ColorField
                label="Accent"
                value={data.brand.accent}
                onChange={(v) => setBrand('accent', v)}
              />
              <ColorField
                label="Title color"
                value={data.brand.titleColor}
                onChange={(v) => setBrand('titleColor', v)}
              />
              <ColorField
                label="CTA text color"
                value={data.brand.ctaTextColor}
                onChange={(v) => setBrand('ctaTextColor', v)}
              />
              <ColorField
                label="Stroke / Arrow"
                value={data.brand.strokeColor}
                onChange={(v) => setBrand('strokeColor', v)}
              />
            </Section>

            {/* ── Action buttons ── */}
            <Stack spacing={1} sx={{ pt: 2 }}>
              {onAddToCampaign && (
                <Button
                  variant="contained"
                  color={addSuccess ? 'success' : 'secondary'}
                  fullWidth
                  size="large"
                  startIcon={
                    addSuccess ? (
                      <IconCheck size={20} />
                    ) : adding ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      <IconPhotoPlus size={20} />
                    )
                  }
                  onClick={handleAddToCampaign}
                  disabled={adding || exporting || addSuccess}
                >
                  {addSuccess
                    ? 'Done!'
                    : adding
                    ? 'Adding...'
                    : `Add to Campaign Media (${format})`}
                </Button>
              )}
              <Button
                variant="outlined"
                fullWidth
                size="large"
                startIcon={exporting ? <CircularProgress size={18} /> : <IconDownload size={20} />}
                onClick={handleDownload}
                disabled={exporting || adding}
              >
                {exporting ? 'Exporting...' : `Download PNG (${format})`}
              </Button>
            </Stack>
          </Stack>
        </Box>

        {/* ── Preview area ── */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            p: 4,
            overflow: 'auto',
            bgcolor: '#F5F5F7',
          }}
        >
          <Stack alignItems="center" spacing={1.5}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ letterSpacing: 0.4, textTransform: 'uppercase' }}
            >
              {isFeed ? 'Feed \u00B7 1080 \u00D7 1080' : 'Story \u00B7 1080 \u00D7 1920'}
            </Typography>
            <Box
              sx={{
                width: wrapperW,
                height: wrapperH,
                overflow: 'hidden',
                borderRadius: 2,
                boxShadow: '0 8px 28px rgba(0,0,0,0.10)',
                bgcolor: '#fff',
                position: 'relative',
              }}
            >
              <iframe
                ref={iframeRef}
                srcDoc={currentTemplate}
                onLoad={handleIframeLoad}
                title="Template Preview"
                sandbox="allow-same-origin"
                style={{
                  width: cardW,
                  height: cardH,
                  border: 'none',
                  transform: `scale(${scale})`,
                  transformOrigin: 'top left',
                  display: 'block',
                }}
              />
            </Box>
          </Stack>
        </Box>
      </Box>
    </Dialog>
  )
}

// ── Build initial data from order ────────────────────────────────────────────

function buildInitialData(orderData?: ImageGeneratorOrderData): CardData {
  return {
    job: {
      ...DEFAULT_JOB,
      city: orderData?.city || '',
      // Company is intentionally left empty by default (not prefilled from order).
      company: '',
      employmentType: orderData?.employmentType || '',
      logoUrl: orderData?.logoUrl || '',
      headerImage: orderData?.headerImage || '',
    },
    brand: { ...DEFAULT_BRANDING },
    displayTitle: orderData?.jobName || '',
    label: '',
    subtitle: '',
    ctaText: DEFAULT_CTA_TEXT,
    ctaRadius: 60,
  }
}

export default ImageGeneratorDialog
